// Section 22 and 24.14: <Sparkline> for per-job projected output. One series in the accent
// (2px line, end dot with a surface ring); an optional baseline series is dashed and muted and
// named in the text beside the chart. The accessible name carries the values.
import { domainOf, linear, pathOf } from './scale';
import './charts.css';

interface Props {
  values: readonly number[];
  /** Comparison series drawn dashed (e.g. no changes this season). */
  baseline?: readonly number[];
  /** Accessible description, including the values. */
  label: string;
  width?: number;
  height?: number;
}

export function Sparkline({ values, baseline, label, width = 120, height = 28 }: Props) {
  const all = [...values, ...(baseline ?? [])];
  const [lo, hi] = domainOf(all);
  const n = Math.max(values.length, baseline?.length ?? 0);
  const pad = 5;
  const x = linear(0, Math.max(1, n - 1), pad, width - pad);
  const y = linear(lo, hi, height - pad, pad);
  const points = (series: readonly number[]): [number, number][] =>
    series.map((v, i) => [x(i), y(v)]);
  const main = points(values);
  const end = main[main.length - 1];
  return (
    <svg
      class="spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <line class="spark__base" x1={pad} x2={width - pad} y1={y(lo)} y2={y(lo)} />
      {baseline !== undefined && baseline.length > 1 && (
        <path class="spark__baseline" d={pathOf(points(baseline))} />
      )}
      {main.length > 1 && <path class="spark__line" d={pathOf(main)} />}
      {end !== undefined && <circle class="spark__dot" cx={end[0]} cy={end[1]} r={3.5} />}
    </svg>
  );
}
